import React, { useState } from 'react';
import { backdropProps } from '../utils/backdropClose';
import { supabase } from '../supabase';
import { CONSULTANT_CONTACT_ROLES } from '../constants/roles';

// AddConsultantModal — add OR edit a consultant. consultant = null -> add; consultant = row -> edit.
//
// ADD: full consultant + >=1 inline contact (exactly one Primary), all-or-nothing; portal_token
//   generated (clean slug + 8 hex); status 'Active'. Failure after the row lands deletes it.
// EDIT: single-row UPDATE of consultant fields only. Contacts are managed elsewhere (D4b) — the
//   contacts section is hidden and its validation skipped. portal_token is NEVER written (the
//   existing portal link must keep working). A rename warning informs (does not block) the user.
//
// Props: isDark; consultant; onClose; onSuccess(consultantRow) — parent re-fetches + drills.
export default function AddConsultantModal({ isDark, consultant, onClose, onSuccess }) {
  const isEdit = !!consultant;
  const emptyContact = () => ({ first_name: '', last_name: '', title: '', email: '', phone: '', roles: [] });
  const [form, setForm] = useState({
    company:                  consultant?.company || '',
    email:                    consultant?.email || '',
    address_line1:            consultant?.address_line1 || '',
    address_line2:            consultant?.address_line2 || '',
    city:                     consultant?.city || '',
    state:                    consultant?.state || '',
    zip:                      consultant?.postal_code || '',
    contract_expiration_date: consultant?.contract_expiration_date || '',
    auto_renew:               consultant?.auto_renew || false,
    services:                 consultant?.services || [],
    fee_structure_notes:      consultant?.fee_structure_notes || '',
    fee_specifics_notes:      consultant?.fee_specifics_notes || '',
  });
  const [contacts, setContacts] = useState([emptyContact()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const c = isDark ? {
    card: '#2F3854', border: '#374060', text: '#e8eaf2',
    muted: 'var(--slate)', input: 'var(--ink)', inputBorder: '#434C68', sub: 'rgba(255,255,255,0.03)',
  } : {
    card: '#ffffff', border: 'var(--line)', text: 'var(--ink)',
    muted: 'var(--slate)', input: '#ffffff', inputBorder: '#c8ccd8', sub: '#f7f8fb',
  };

  const s = {
    label:    { display:'block', fontSize:'11px', fontWeight:'700', color:c.muted, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' },
    input:    { width:'100%', padding:'8px 11px', background:c.input, border:`1px solid ${c.inputBorder}`, borderRadius:'7px', color:c.text, fontSize:'13px', outline:'none', boxSizing:'border-box', fontFamily:"'Public Sans',system-ui,sans-serif" },
    textarea: { width:'100%', padding:'8px 11px', background:c.input, border:`1px solid ${c.inputBorder}`, borderRadius:'7px', color:c.text, fontSize:'13px', outline:'none', boxSizing:'border-box', fontFamily:"'Public Sans',system-ui,sans-serif", minHeight:'58px', resize:'vertical' },
    btn:      { padding:'9px 18px', background:'linear-gradient(135deg,#B40000,#9C0000)', border:'none', borderRadius:'7px', color:'white', fontSize:'13px', fontWeight:'700', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" },
    btnGhost: { padding:'9px 16px', background:'transparent', border:`1px solid ${c.border}`, borderRadius:'7px', color:c.muted, fontSize:'13px', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" },
    sectionH: { fontSize:'12px', fontWeight:'700', color:c.text, textTransform:'uppercase', letterSpacing:'0.06em', margin:'22px 0 12px', paddingBottom:'6px', borderBottom:`1px solid ${c.border}` },
    pill: (on) => ({ padding:'5px 12px', borderRadius:'999px', fontSize:'12px', fontWeight:'700', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif",
      border: on ? '1px solid #B40000' : `1px solid ${c.inputBorder}`, background: on ? 'rgba(180,0,0,0.1)' : 'transparent', color: on ? 'var(--red)' : c.muted }),
  };

  // Per Steph's sketch: services is a fixed multi-check list (free text would fragment the data).
  const SERVICE_OPTIONS = ['RE Appeals', 'BPP Appeals', 'RE Compliance', 'BPP Compliance', 'Attorney'];

  function set(field, val) { setForm(prev => ({ ...prev, [field]: val })); }
  function toggleService(name) { setForm(prev => ({ ...prev, services: prev.services.includes(name) ? prev.services.filter(x => x !== name) : [...prev.services, name] })); }
  function setContact(i, field, val) { setContacts(prev => prev.map((ct, idx) => idx === i ? { ...ct, [field]: val } : ct)); }
  function toggleContactRole(i, key) {
    setContacts(prev => prev.map((ct, idx) => idx === i ? { ...ct, roles: ct.roles.includes(key) ? ct.roles.filter(r => r !== key) : [...ct.roles, key] } : ct));
  }
  function addContact() { setContacts(prev => [...prev, emptyContact()]); }
  function removeContact(i) { setContacts(prev => prev.filter((_, idx) => idx !== i)); }

  // Clean slug + 8 random hex. The 8-hex carries uniqueness; the slug is cosmetic (URL-friendly).

  function validate() {
    if (!form.company.trim()) return 'Company name is required.';
    // Address, contract expiration and fee notes are optional (Philip 2026-11-03); a blank saves as null.
    if (form.services.length === 0) return 'Select at least one service.';
    if (isEdit) return null; // contacts are managed separately (D4b) — skip contact validation in edit mode
    if (contacts.length === 0) return 'Add at least one contact.';
    for (let i = 0; i < contacts.length; i++) {
      const ct = contacts[i];
      if (!ct.first_name.trim() || !ct.last_name.trim()) return `Contact ${i + 1}: first and last name are required.`;
      if (ct.roles.length === 0) return `Contact ${i + 1}: select at least one role.`;
    }
    const primaryCount = contacts.filter(ct => ct.roles.includes('primary')).length;
    if (primaryCount === 0) return 'Exactly one contact must be the Primary contact.';
    if (primaryCount > 1) return 'Only one contact can be the Primary contact.';
    return null;
  }

  // Consultant column values shared by add (insert) and edit (update). portal_token NOT included.
  function consultantFields() {
    return {
      company:                  form.company.trim(),
      email:                    form.email.trim() || null,
      address_line1:            form.address_line1.trim() || null,
      address_line2:            form.address_line2.trim() || null,
      city:                     form.city.trim() || null,
      state:                    form.state.trim() || null,
      postal_code:              form.zip.trim() || null,
      contract_expiration_date: form.contract_expiration_date || null,
      auto_renew:               form.auto_renew,
      services:                 form.services,
      fee_structure_notes:      form.fee_structure_notes.trim() || null,
      fee_specifics_notes:      form.fee_specifics_notes.trim() || null,
    };
  }

  async function handleSave() {
    const v = validate();
    if (v) { setError(v); return; }
    setSaving(true);
    setError('');

    if (isEdit) {
      try {
        // EDIT: single-row update of consultant fields only. portal_token untouched. No contacts.
        const { data: updated, error: uErr } = await supabase.from('consultants')
          .update(consultantFields()).eq('id', consultant.id).select().single();
        if (uErr) throw uErr;
        onSuccess(updated);
      } catch (e) {
        setError(e?.message || 'Save failed.');
      } finally {
        setSaving(false);
      }
      return;
    }

    // ADD: insert consultant (+ status + generated portal_token), then inline contacts/roles.
    let consultantId = null;
    try {
      const { data: con, error: cErr } = await supabase.from('consultants').insert({
        ...consultantFields(),
        status:       'Active',
      }).select().single();
      if (cErr) throw cErr;
      consultantId = con.id;

      for (const ct of contacts) {
        const { data: contact, error: ctErr } = await supabase.from('consultant_contacts').insert({
          consultant_id: consultantId,
          first_name:    ct.first_name.trim(),
          last_name:     ct.last_name.trim(),
          title:         ct.title.trim() || null,
          email:         ct.email.trim() || null,
          phone:         ct.phone.trim() || null,
        }).select().single();
        if (ctErr) throw ctErr;
        const roleRows = ct.roles.map(role => ({ contact_id: contact.id, consultant_id: consultantId, role }));
        const { error: rErr } = await supabase.from('consultant_contact_roles').insert(roleRows);
        if (rErr) throw rErr;
      }

      onSuccess(con);
    } catch (e) {
      // All-or-nothing: if the consultant row landed, delete it (cascade wipes any contacts/roles).
      if (consultantId) { await supabase.from('consultants').delete().eq('id', consultantId); }
      setError((e?.message || 'Save failed') + ' — no consultant was created.');
    } finally {
      setSaving(false);
    }
  }

  const renamed = isEdit && form.company.trim() !== consultant.company;

  return (
    <div {...backdropProps(onClose)}
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background:c.card, borderRadius:'14px', padding:'28px', maxWidth:'640px', width:'100%', boxShadow:'0 8px 40px rgba(0,0,0,0.3)', maxHeight:'calc(100vh - 40px)', overflowY:'auto', fontFamily:"'Public Sans',system-ui,sans-serif" }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'4px' }}>
          <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'20px', fontWeight:'700', color:c.text }}>{isEdit ? 'Edit Consultant' : 'Add Consultant'}</div>
          <button onClick={onClose} type="button" style={{ background:'none', border:'none', color:c.muted, fontSize:'18px', cursor:'pointer', padding:'0 4px', lineHeight:1 }}>✕</button>
        </div>
        <div style={{ fontSize:'12px', color:c.muted, marginBottom:'10px' }}>{isEdit ? "Update this consultant's details." : 'Create a consultant firm and its contacts. A portal link is generated automatically.'}</div>

        {/* Company + email */}
        <div style={{ marginBottom:'14px' }}>
          <label style={s.label}>Company *</label>
          <input style={s.input} value={form.company} onChange={e => set('company', e.target.value)} placeholder="e.g. Archer Attorneys at Law" />
          {renamed && (
            <div style={{ marginTop:'6px', fontSize:'12px', color:'#d97706' }}>
              ⚠ Renaming will disconnect this consultant's existing property assignments, upload links, and history (matched by name) until the data is updated.
            </div>
          )}
        </div>
        <div style={{ marginBottom:'14px' }}>
          <label style={s.label}>Email</label>
          <input style={s.input} value={form.email} onChange={e => set('email', e.target.value)} placeholder="firm@example.com" />
        </div>

        <div style={s.sectionH}>Address</div>
        <div style={{ marginBottom:'14px' }}>
          <label style={s.label}>Address Line 1</label>
          <input style={s.input} value={form.address_line1} onChange={e => set('address_line1', e.target.value)} />
        </div>
        <div style={{ marginBottom:'14px' }}>
          <label style={s.label}>Address Line 2</label>
          <input style={s.input} value={form.address_line2} onChange={e => set('address_line2', e.target.value)} />
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:'10px', marginBottom:'14px' }}>
          <div><label style={s.label}>City</label><input style={s.input} value={form.city} onChange={e => set('city', e.target.value)} /></div>
          <div><label style={s.label}>State</label><input style={s.input} value={form.state} onChange={e => set('state', e.target.value)} /></div>
          <div><label style={s.label}>Zip</label><input style={s.input} value={form.zip} onChange={e => set('zip', e.target.value)} /></div>
        </div>

        <div style={s.sectionH}>Contract &amp; Services</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'14px', alignItems:'end' }}>
          <div><label style={s.label}>Contract Expiration</label><input type="date" style={s.input} value={form.contract_expiration_date} onChange={e => set('contract_expiration_date', e.target.value)} /></div>
          <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'13px', color:c.text, cursor:'pointer', paddingBottom:'8px' }}>
            <input type="checkbox" checked={form.auto_renew} onChange={e => set('auto_renew', e.target.checked)} style={{ cursor:'pointer' }} />
            Auto-renews
          </label>
        </div>
        <div style={{ marginBottom:'14px' }}>
          <label style={s.label}>Services *</label>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'2px' }}>
            {SERVICE_OPTIONS.map(name => {
              const on = form.services.includes(name);
              return <button key={name} type="button" onClick={() => toggleService(name)} style={s.pill(on)}>{on ? '✓ ' : ''}{name}</button>;
            })}
          </div>
        </div>
        <div style={{ marginBottom:'14px' }}>
          <label style={s.label}>Fee Structure Notes</label>
          <textarea style={s.textarea} value={form.fee_structure_notes} onChange={e => set('fee_structure_notes', e.target.value)} />
        </div>
        <div style={{ marginBottom:'14px' }}>
          <label style={s.label}>Fee Specifics Notes</label>
          <textarea style={s.textarea} value={form.fee_specifics_notes} onChange={e => set('fee_specifics_notes', e.target.value)} />
        </div>

        {/* Contacts — ADD mode only (edit manages contacts via the All Contacts table). */}
        {!isEdit && (
          <>
            <div style={s.sectionH}>Contacts * <span style={{ textTransform:'none', fontWeight:'400', color:c.muted }}>(at least one; exactly one Primary)</span></div>
            {contacts.map((ct, i) => (
              <div key={i} style={{ background:c.sub, border:`1px solid ${c.border}`, borderRadius:'10px', padding:'14px', marginBottom:'12px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                  <div style={{ fontSize:'12px', fontWeight:'700', color:c.text }}>Contact {i + 1}</div>
                  {contacts.length > 1 && <button type="button" onClick={() => removeContact(i)} style={{ background:'none', border:'none', color:'#f87171', fontSize:'12px', cursor:'pointer' }}>Remove</button>}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px' }}>
                  <div><label style={s.label}>First Name *</label><input style={s.input} value={ct.first_name} onChange={e => setContact(i, 'first_name', e.target.value)} /></div>
                  <div><label style={s.label}>Last Name *</label><input style={s.input} value={ct.last_name} onChange={e => setContact(i, 'last_name', e.target.value)} /></div>
                </div>
                <div style={{ marginBottom:'10px' }}><label style={s.label}>Title</label><input style={s.input} value={ct.title} onChange={e => setContact(i, 'title', e.target.value)} /></div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px' }}>
                  <div><label style={s.label}>Email</label><input style={s.input} value={ct.email} onChange={e => setContact(i, 'email', e.target.value)} /></div>
                  <div><label style={s.label}>Phone</label><input style={s.input} value={ct.phone} onChange={e => setContact(i, 'phone', e.target.value)} placeholder="(000) 000-0000" /></div>
                </div>
                <label style={s.label}>Roles *</label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'2px' }}>
                  {CONSULTANT_CONTACT_ROLES.map(r => {
                    const on = ct.roles.includes(r.key);
                    return <button key={r.key} type="button" onClick={() => toggleContactRole(i, r.key)} style={s.pill(on)}>{on ? '✓ ' : ''}{r.label}</button>;
                  })}
                </div>
              </div>
            ))}
            <button type="button" onClick={addContact} style={{ ...s.btnGhost, fontSize:'12px', padding:'7px 14px' }}>+ Add another contact</button>
          </>
        )}

        {error && (
          <div style={{ padding:'10px 12px', margin:'18px 0 0', background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:'7px', fontSize:'12px', color:'#f87171' }}>
            ⚠ {error}
          </div>
        )}

        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'22px' }}>
          <button type="button" onClick={onClose} disabled={saving} style={s.btnGhost}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving} style={{ ...s.btn, opacity: saving ? 0.6 : 1, cursor: saving ? 'default' : 'pointer' }}>
            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : '+ Add Consultant')}
          </button>
        </div>
      </div>
    </div>
  );
}
